import { api } from './api';

/**
 * Configuration endpoints, injected separately from the activity endpoints so
 * neither file grows unbounded. Same cache, same tags.
 */

export interface RestaurantRecord {
  _id: string;
  name: string;
  cuisine?: string;
  website?: string;
  mainPhone?: string;
  description?: string;
}

export interface LocationRecord {
  /* Payment and POS configuration. */
  posProvider?: 'CLOVER' | 'SKYTAB' | 'GIVEX' | 'NONE';
  paymentProvider?: 'SHIFT4' | 'CLOVER' | 'PAY_AT_RESTAURANT' | 'NONE';
  paymentPolicyMode?: 'NEVER' | 'ALWAYS' | 'REQUIRE_ABOVE_AMOUNT';
  paymentThresholdCents?: number;
  paymentThresholdOperator?: 'GREATER_THAN' | 'GREATER_THAN_OR_EQUAL';
  autoSendPaymentLink?: boolean;
  paymentLinkExpiryMinutes?: number;
  unpaidOrderAction?: 'KEEP_OPEN' | 'MARK_FOR_REVIEW' | 'CANCEL_POS_ORDER_IF_SUPPORTED';

  _id: string;
  name: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  region: string;
  country: string;
  postalCode: string;
  timezone: string;
  transferNumber?: string;
  fulfillment: string[];
  /** What kind of business answers: shapes the assistant's wording. */
  vertical?: 'RESTAURANT' | 'RETAIL' | 'GYM' | 'CLINIC' | 'SALON';
  averagePrepTimeMinutes: number;
  parkingInfo?: string;
  deliveryInfo?: string;
  pickupInfo?: string;
  directionsInfo?: string;
  acceptedPaymentMethods: string[];
  orderingEnabled: boolean;
  recordingEnabled: boolean;

  /* --------------------------------------------- voice and conversation */
  voiceProfileId?: string;
  speakingSpeed?: number;
  language?: string;
  responseStyle?: 'WARM' | 'BRIEF' | 'FORMAL';
  bargeInEnabled?: boolean;
  speechStartTimeoutMs?: number;
  endOfSpeechSilenceMs?: number;
  noInputTimeoutMs?: number;
  maximumRetries?: number;
}

export interface HourWindow {
  open: number;
  close: number;
}

export interface BusinessHourRecord {
  _id: string;
  weekday: number;
  closed: boolean;
  windows: HourWindow[];
}

export interface PronunciationTerm {
  _id: string;
  display: string;
  spoken: string;
  enabled: boolean;
}

export interface SpokenNameEntry {
  id: string;
  kind: 'MENU_ITEM' | 'MODIFIER';
  display: string;
  spoken: string;
}

export interface VoiceSettingsPatch {
  voiceProfileId?: string;
  speakingSpeed?: number;
  language?: string;
  responseStyle?: 'WARM' | 'BRIEF' | 'FORMAL';
  bargeInEnabled?: boolean;
  endOfSpeechSilenceMs?: number;
  speechStartTimeoutMs?: number;
}

export interface HolidayRecord {
  _id: string;
  name: string;
  date: string;
  closed: boolean;
  windows: HourWindow[];
  message?: string;
}

export interface FaqRecord {
  _id: string;
  canonicalQuestion: string;
  acceptedPhrases: string[];
  keywordSets: string[][];
  answer: string;
  enabled: boolean;
  priority: number;
}

export interface PromptRecord {
  key: string;
  defaultText: string;
  text: string;
  customised: boolean;
  audioReady: boolean;
  enabled: boolean;
}

export interface PhoneNumberRecord {
  _id: string;
  number: string;
  status: string;
  source: string;
  locationId?: string;
  webhookHealthy: boolean;
  lastWebhookAt?: string;
}

export interface AvailableNumber {
  phoneNumber: string;
  locality?: string;
  region?: string;
  monthlyCostCents: number;
}

export interface OwnedNumberLookup {
  telnyxNumberId: string;
  phoneNumber: string;
  status: string;
  connectionId?: string;
  connectedToApp: boolean;
}

export interface OnboardingProgress {
  step: number;
  completedAt?: string;
  restaurantId?: string;
  locationId?: string;
  checks: {
    restaurant: boolean;
    location: boolean;
    hours: boolean;
    faqs: boolean;
    phoneNumber: boolean;
    transferNumber: boolean;
  };
  readyForGoLive: boolean;
}

export interface TestVoice {
  voiceId: string;
  name: string;
  accent: string;
  gender: string;
}

export interface TestVoicesResponse {
  data: TestVoice[];
  defaultVoiceId: string;
  /** Which engine speaks: kokoro (self-hosted), deepgram or gemini. */
  provider: 'kokoro' | 'deepgram' | 'elevenlabs' | 'smallest' | 'telnyx' | 'gemini';
  persona: string;
  available: boolean;
  maxSessionSeconds: number;
}

export interface TestSessionResponse {
  callId: string;
  roomName: string;
  url: string;
  token: string;
  voiceId: string;
  persona: string;
  maxSessionSeconds: number;
}

export interface PronunciationTestResult {
  /** MP3, base64. */
  audio: string;
  contentType: string;
  /** Exactly what the voice was given after the dictionary was applied. */
  spokenText: string;
  /** The saved fix for this exact word, if there is one. */
  savedSpoken: string | null;
  voiceId: string;
  provider: 'kokoro' | 'deepgram' | 'elevenlabs' | 'smallest' | 'telnyx' | 'gemini';
}

export const configApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /* ---------------------------------------------------------- onboarding */
    getOnboarding: builder.query<OnboardingProgress, void>({
      query: () => '/onboarding/progress',
      providesTags: ['Onboarding']
    }),
    saveOnboardingStep: builder.mutation<{ step: number }, { step: number }>({
      query: (body) => ({ url: '/onboarding/step', method: 'POST', body }),
      invalidatesTags: ['Onboarding']
    }),
    goLive: builder.mutation<{ live: boolean }, { locationId: string }>({
      query: (body) => ({ url: '/onboarding/go-live', method: 'POST', body }),
      invalidatesTags: ['Onboarding', 'Location', 'Session']
    }),

    /* -------------------------------------------------- restaurants/places */
    getRestaurants: builder.query<{ data: RestaurantRecord[] }, void>({
      query: () => '/restaurants',
      providesTags: ['Restaurant']
    }),
    createRestaurant: builder.mutation<
      { _id: string; name: string },
      { name: string; cuisine?: string; website?: string; mainPhone?: string }
    >({
      query: (body) => ({ url: '/restaurants', method: 'POST', body }),
      invalidatesTags: ['Restaurant', 'Onboarding']
    }),

    createLocation: builder.mutation<LocationRecord, Partial<LocationRecord> & {
      restaurantId: string;
      name: string;
    }>({
      query: (body) => ({ url: '/locations', method: 'POST', body }),
      invalidatesTags: ['Location', 'Onboarding']
    }),
    getLocation: builder.query<
      LocationRecord & { hours: BusinessHourRecord[]; holidays: HolidayRecord[] },
      string
    >({
      query: (id) => `/locations/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Location', id }]
    }),
    updateRestaurant: builder.mutation<
      RestaurantRecord,
      { id: string } & Partial<RestaurantRecord>
    >({
      query: ({ id, ...body }) => ({ url: `/restaurants/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Restaurant', 'Onboarding']
    }),
    updateLocation: builder.mutation<
      LocationRecord,
      { id: string } & Partial<LocationRecord>
    >({
      query: ({ id, ...body }) => ({ url: `/locations/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Location', 'Onboarding']
    }),
    setBusinessHours: builder.mutation<
      { data: BusinessHourRecord[] },
      { id: string; days: { weekday: number; closed: boolean; windows: HourWindow[] }[] }
    >({
      query: ({ id, days }) => ({ url: `/locations/${id}/hours`, method: 'PUT', body: { days } }),
      invalidatesTags: ['Location', 'Onboarding']
    }),

    createHoliday: builder.mutation<
      HolidayRecord,
      { locationId: string; name: string; date: string; closed: boolean; message?: string }
    >({
      query: ({ locationId, ...body }) => ({
        url: `/locations/${locationId}/holidays`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['Location']
    }),
    deleteHoliday: builder.mutation<void, { locationId: string; holidayId: string }>({
      query: ({ locationId, holidayId }) => ({
        url: `/locations/${locationId}/holidays/${holidayId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Location']
    }),

    /* --------------------------------------------------------------- FAQs */
    getFaqs: builder.query<{ data: FaqRecord[] }, string>({
      query: (locationId) => `/faqs?locationId=${locationId}`,
      providesTags: ['Faq']
    }),
    createFaq: builder.mutation<FaqRecord, Partial<FaqRecord> & { locationId: string }>({
      query: (body) => ({ url: '/faqs', method: 'POST', body }),
      invalidatesTags: ['Faq', 'Onboarding']
    }),
    updateFaq: builder.mutation<FaqRecord, { id: string } & Partial<FaqRecord>>({
      query: ({ id, ...body }) => ({ url: `/faqs/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Faq']
    }),
    deleteFaq: builder.mutation<void, string>({
      query: (id) => ({ url: `/faqs/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Faq', 'Onboarding']
    }),
    testFaq: builder.mutation<
      { matched: boolean; answer?: string; confidence?: number; fallback?: string },
      { phrase: string; locationId: string }
    >({
      query: (body) => ({ url: '/faqs/test', method: 'POST', body })
    }),

    /* -------------------------------------------------------------- voice */
    getPrompts: builder.query<{ data: PromptRecord[] }, string>({
      query: (locationId) => `/voice/prompts?locationId=${locationId}`,
      providesTags: ['Prompt']
    }),
    savePrompt: builder.mutation<
      PromptRecord,
      { locationId: string; key: string; text: string; enabled?: boolean }
    >({
      query: (body) => ({ url: '/voice/prompts', method: 'PUT', body }),
      invalidatesTags: ['Prompt']
    }),

    getVoiceProfiles: builder.query<
      { data: { _id: string; name: string; language: string; accent?: string }[] },
      void
    >({
      query: () => '/voice/profiles',
      providesTags: ['Prompt']
    }),
    getPronunciations: builder.query<
      { terms: PronunciationTerm[]; menuItems: SpokenNameEntry[]; modifiers: SpokenNameEntry[] },
      string
    >({
      query: (locationId) => `/voice/pronunciations?locationId=${locationId}`,
      providesTags: ['Pronunciation']
    }),
    savePronunciation: builder.mutation<
      PronunciationTerm,
      { locationId: string; display: string; spoken: string; enabled?: boolean }
    >({
      query: (body) => ({ url: '/voice/pronunciations', method: 'PUT', body }),
      invalidatesTags: ['Pronunciation', 'Prompt']
    }),
    deletePronunciation: builder.mutation<void, string>({
      query: (id) => ({ url: `/voice/pronunciations/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Pronunciation', 'Prompt']
    }),
    setSpokenName: builder.mutation<
      { id: string; spokenName: string },
      { kind: 'MENU_ITEM' | 'MODIFIER'; id: string; spokenName: string }
    >({
      query: (body) => ({ url: '/voice/spoken-name', method: 'PUT', body }),
      invalidatesTags: ['Pronunciation', 'Menu']
    }),
    previewVoice: builder.mutation<
      { audioUrl: string | null; spokenFallback: string | null; provider: string; latencyMs: number },
      { locationId: string; text: string }
    >({
      query: (body) => ({ url: '/voice/preview', method: 'POST', body })
    }),
    regenerateAudio: builder.mutation<
      { rendered?: number; message: string },
      { locationId: string }
    >({
      query: (body) => ({ url: '/voice/regenerate', method: 'POST', body }),
      invalidatesTags: ['Prompt']
    }),

    /* -------------------------------------------------------------- phone */
    getPhoneNumbers: builder.query<{ data: PhoneNumberRecord[] }, void>({
      query: () => '/phones',
      providesTags: ['Phone']
    }),
    searchPhoneNumbers: builder.query<
      { data: AvailableNumber[] },
      { countryCode?: string; areaCode?: string; limit?: number }
    >({
      query: (params) => ({ url: '/phones/search', params })
    }),
    buyPhoneNumber: builder.mutation<
      PhoneNumberRecord,
      { phoneNumber: string; locationId: string }
    >({
      query: (body) => ({ url: '/phones/purchase', method: 'POST', body }),
      invalidatesTags: ['Phone', 'Onboarding']
    }),
    lookupPhoneNumber: builder.query<OwnedNumberLookup, { phoneNumber: string }>({
      query: (params) => ({ url: '/phones/lookup', params })
    }),
    connectPhoneNumber: builder.mutation<
      PhoneNumberRecord,
      { phoneNumber: string; telnyxNumberId?: string; locationId: string }
    >({
      query: (body) => ({ url: '/phones/connect-existing', method: 'POST', body }),
      invalidatesTags: ['Phone', 'Onboarding']
    }),

    /* ------------------------------------------------- browser test call */
    testPronunciation: builder.mutation<
      PronunciationTestResult,
      { locationId: string; text: string; voiceId?: string; trySpoken?: string }
    >({
      query: (body) => ({ url: '/voice/pronunciations/test', method: 'POST', body })
    }),
    getTestVoices: builder.query<TestVoicesResponse, void>({
      query: () => '/voice/test-voices'
    }),
    startTestSession: builder.mutation<TestSessionResponse, { locationId: string; voiceId?: string }>({
      query: (body) => ({ url: '/voice/test-session', method: 'POST', body })
    }),
    stopTestSession: builder.mutation<{ ok: true }, string>({
      query: (callId) => ({ url: `/voice/test-session/${callId}/stop`, method: 'POST' }),
      invalidatesTags: ['Call']
    })
  })
});

export const {
  useGetOnboardingQuery,
  useSaveOnboardingStepMutation,
  useGoLiveMutation,
  useGetRestaurantsQuery,
  useCreateRestaurantMutation,
  useCreateLocationMutation,
  useGetLocationQuery,
  useUpdateRestaurantMutation,
  useUpdateLocationMutation,
  useSetBusinessHoursMutation,
  useCreateHolidayMutation,
  useDeleteHolidayMutation,
  useGetFaqsQuery,
  useCreateFaqMutation,
  useUpdateFaqMutation,
  useDeleteFaqMutation,
  useTestFaqMutation,
  useGetPromptsQuery,
  useSavePromptMutation,
  useGetVoiceProfilesQuery,
  useGetPronunciationsQuery,
  useSavePronunciationMutation,
  useDeletePronunciationMutation,
  useSetSpokenNameMutation,
  usePreviewVoiceMutation,
  useTestPronunciationMutation,
  useRegenerateAudioMutation,
  useGetPhoneNumbersQuery,
  useLazySearchPhoneNumbersQuery,
  useBuyPhoneNumberMutation,
  useLazyLookupPhoneNumberQuery,
  useConnectPhoneNumberMutation,
  useGetTestVoicesQuery,
  useStartTestSessionMutation,
  useStopTestSessionMutation
} = configApi;
